import { safeSetItem } from "@/lib/safe-storage";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Check, Copy, RotateCcw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/AppShell";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { PeoplePanel } from "@/components/PeoplePanel";
import { AiUsagePanel } from "@/components/AiUsagePanel";
import { PlanPanel } from "@/components/PlanPanel";
import { SettingsPanel } from "@/components/SettingsPanel";
import { ClassificationPanel } from "@/components/ClassificationPanel";
import { TimeOffPanel } from "@/components/TimeOffPanel";
import { ReminderPrefsPanel } from "@/components/ReminderPrefsPanel";
import { CalendarFeedPanel } from "@/components/CalendarFeedPanel";

import { supabase } from "@/integrations/supabase/client";
import { resetOnboarding } from "@/lib/onboarding";
import { BusinessProfilePanel } from "@/components/onboarding/BusinessProfilePanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  LAST_CODE_STORAGE_KEY,
  PIN_LENGTH,
  codeToEmail,
  digitsOnly,
  emailToCode,
  isValidPin,
} from "@/lib/access-code";

export const Route = createFileRoute("/_authenticated/account")({
  head: () => ({
    meta: [
      { title: "Account — MyPriorities" },
      {
        name: "description",
        content: "View your access code and change the PIN that unlocks your hub.",
      },
      { property: "og:title", content: "Account — MyPriorities" },
      { property: "og:description", content: "Your access code and PIN settings." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AccountPage,
});

function AccountPage() {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState<string | null>(null);
  const [savingName, setSavingName] = useState(false);
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: code } = useQuery({
    queryKey: ["access-code"],
    queryFn: async () => {
      const { data } = await supabase.auth.getUser();
      const fromEmail = emailToCode(data.user?.email);
      if (fromEmail) return fromEmail;
      const meta = data.user?.user_metadata as { access_code?: string } | undefined;
      if (meta?.access_code) return meta.access_code;
      const { data: profile } = await supabase.from("profiles").select("access_code").maybeSingle();
      return profile?.access_code ?? null;
    },
  });

  const { data: profileName } = useQuery({
    queryKey: ["display-name"],
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("display_name").maybeSingle();
      return data?.display_name ?? "";
    },
  });

  async function saveName() {
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) return;
    setSavingName(true);
    const { error } = await supabase
      .from("profiles")
      .update({ display_name: (name ?? profileName ?? "").trim() || null })
      .eq("user_id", auth.user.id);
    setSavingName(false);
    if (error) {
      toast.error("Couldn't save your name");
      return;
    }
    setName(null);
    await queryClient.invalidateQueries({ queryKey: ["display-name"] });
    toast.success("Name saved.");
  }

  async function copyCode() {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please write the code down.");
    }
  }

  async function changePin(e: React.FormEvent) {
    e.preventDefault();
    if (newPin !== confirmPin) {
      toast.error("The two new PINs don't match.");
      return;
    }
    if (!code) {
      toast.error("Couldn't confirm your access code.");
      return;
    }
    setBusy(true);
    try {
      // Re-check the current PIN before changing it.
      const { error: checkError } = await supabase.auth.signInWithPassword({
        email: codeToEmail(code),
        password: currentPin,
      });
      if (checkError) throw new Error("That current PIN isn't right.");

      const { error } = await supabase.auth.updateUser({ password: newPin });
      if (error) throw error;

      safeSetItem(LAST_CODE_STORAGE_KEY, code);
      setCurrentPin("");
      setNewPin("");
      setConfirmPin("");
      toast.success("PIN updated.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  const ready = isValidPin(currentPin) && isValidPin(newPin) && isValidPin(confirmPin);

  return (
    <AppShell title="Account" subtitle="Your access code, plan and PIN.">
      <PaymentTestModeBanner />
      <div className="mt-4 grid gap-6 md:grid-cols-2">
        <div className="md:col-span-2">
          <PlanPanel />
        </div>

        <div className="md:col-span-2">
          <AiUsagePanel />
        </div>

        <section className="panel p-6">
          <p className="label-caps">Access code</p>
          <p className="mt-3 font-mono text-2xl font-bold tracking-[0.2em] text-primary">
            {code ?? "—"}
          </p>
          <div className="mt-5 space-y-2">
            <Label htmlFor="display-name">Your name</Label>
            <div className="flex gap-2">
              <Input
                id="display-name"
                placeholder="e.g. Mark Jones"
                value={name ?? profileName ?? ""}
                onChange={(e) => setName(e.target.value)}
              />
              <Button variant="outline" onClick={() => void saveName()} disabled={savingName}>
                Save
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Shown alongside your access code so teammates recognise you.
            </p>
          </div>

          <Button variant="outline" size="sm" className="mt-4" onClick={copyCode} disabled={!code}>
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            {copied ? "Copied" : "Copy code"}
          </Button>

          <div className="mt-5 flex gap-2 rounded-md border border-border bg-surface p-3 text-xs text-muted-foreground">
            <ShieldAlert className="mt-0.5 size-4 shrink-0 text-warning" />
            <p>
              This account has no email, so there's no reset link. Keep the code and PIN somewhere
              safe — losing both means the hub can't be recovered.
            </p>
          </div>
        </section>

        <section className="panel p-6">
          <h2 className="font-semibold">Change PIN</h2>
          <p className="mt-1 text-sm text-muted-foreground">Your access code stays the same.</p>
          <form onSubmit={changePin} className="mt-5 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-pin">Current PIN</Label>
              <Input
                id="current-pin"
                type="password"
                inputMode="numeric"
                autoComplete="current-password"
                placeholder={"•".repeat(PIN_LENGTH)}
                className="font-mono tracking-[0.4em]"
                value={currentPin}
                onChange={(e) => setCurrentPin(digitsOnly(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="account-new-pin">New PIN</Label>
              <Input
                id="account-new-pin"
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                placeholder={"•".repeat(PIN_LENGTH)}
                className="font-mono tracking-[0.4em]"
                value={newPin}
                onChange={(e) => setNewPin(digitsOnly(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="account-confirm-pin">Confirm new PIN</Label>
              <Input
                id="account-confirm-pin"
                type="password"
                inputMode="numeric"
                autoComplete="new-password"
                placeholder={"•".repeat(PIN_LENGTH)}
                className="font-mono tracking-[0.4em]"
                value={confirmPin}
                onChange={(e) => setConfirmPin(digitsOnly(e.target.value))}
              />
            </div>
            <Button type="submit" disabled={busy || !ready}>
              Update PIN
            </Button>
          </form>
        </section>

        <BusinessProfilePanel />

        <SettingsPanel />


        <ClassificationPanel />

        <ReminderPrefsPanel />

        <CalendarFeedPanel />

        <TimeOffPanel />


        <PeoplePanel />

        <section className="panel p-6">
          <p className="label-caps">Walkthrough</p>
          <p className="mt-3 text-sm text-muted-foreground">
            Replay the quick tour and pick a new starting point — customers and deals, contracts and
            renewals, revenue and sign-offs, or your own priorities. It also brings back the
            guidance on each page.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => {
              resetOnboarding();
              toast.success("Walkthrough reset — it'll show next time you open a page.");
            }}
          >
            <RotateCcw className="size-4" /> Replay walkthrough
          </Button>
        </section>
      </div>
    </AppShell>
  );
}
